import styled from '@emotion/styled'
import { getPriceFromCalculations } from '../../../pure-js/libs/calculateLogic/CalculateDiscountHelpers'
import {
  Calculation,
  CalculationDiscount,
  DiscountType
} from '../../../pure-js/libs/calculateLogic/CalculateDiscountTypes'
import { EIGHT_PX, FOUR_PX, TWELVE_PX } from '../enums/Spacings'
import { getGrooverText } from '../libs/GrooverTextRepository'
import Box from './Box'
import GrooverText from './GrooverText'
import { SkeletonCustom } from './FigmaText'
import PriceContainerActivity from './PriceContainerActivity'
import { State } from '../hooks/useAppState'
import { Currency, Client } from '../../../pure-js/types/GrooverTypes'
import { DEFAULT_CURRENCY } from '../../../pure-js/libs/currencyHelper'
import { formatPrice } from '../utils/activityUtils'
import type { FreeDropinRewardPreview as RewardPreview } from '../hooks/useFreeDropinRewardPreview'
import FreeDropinRewardPreview from './FreeDropinRewardPreview'

type Props = {
  currency: Currency
  userPrices: Calculation[]
  userName: string
  isLoading: boolean
  state?: State
  client?: Client
  freeDropinRewardPreview?: RewardPreview
}

// we want to skip some discount types from the UI
type DisplayableDiscountType = Exclude<DiscountType, 'none' | 'multi_course_matrix' | 'combined'>

// Labels for discount types
const DISCOUNT_CONFIG: Record<DisplayableDiscountType, { textKey: string }> = {
  student: { textKey: 'componentsPricingControlStudentDiscountRow' },
  sibling: { textKey: 'registrationStep1SiblingDiscountCheckboxLabel' },
  senior: { textKey: 'componentsPricingControlSeniorDiscountRow' },
  couples: { textKey: 'componentsPricingControlCoupleDiscountRow' },
  member: { textKey: 'componentsPricingControlMemberDiscountRow' },
  advanced: { textKey: 'adminGeneralEnumsDiscountsDiscountSubEvent' },
  early_bird: { textKey: 'adminGeneralEnumsDiscountsDiscountEarlyBird' },
  combo: { textKey: 'componentsPricingControlComboDiscountRow' },
  price_override: { textKey: 'adminGeneralEnumsDiscountsDiscountEarlyBird' },
  code: { textKey: 'adminGeneralInputFieldsDiscountCodeCode' },
  continuation: { textKey: 'editClientDiscountContinuationTitle' }
}

// Types that should be displayed in the UI summary
//type DisplayableDiscountType = keyof typeof DISCOUNT_CONFIG

const UserPricing = ({ currency, userPrices, userName, isLoading, state, client, freeDropinRewardPreview }: Props) => {
  // Create a record to track all possible discount types
  const initialDiscounts: Record<DiscountType, number> = {} as Record<DiscountType, number>

  // Initialize all discount types to 0
  Object.values(userPrices)
    .flatMap((calc) => calc.discounts || [])
    .forEach((d) => {
      if (initialDiscounts[d.type] === undefined) {
        initialDiscounts[d.type] = 0
      }
    })

  // Calculate all discounts up front with type safety
  const discountTotals = userPrices.reduce((acc, calculation) => {
    if (calculation.discounts) {
      calculation.discounts.forEach((discount) => {
        acc[discount.type] = (acc[discount.type] || 0) + discount.discount
      })
    }
    return acc
  }, initialDiscounts)

  const totalPriceInCents = getPriceFromCalculations(userPrices)

  const getDiscount = (discountType: DiscountType) => {
    const calculation = userPrices[0]
    if (calculation?.discounts) {
      return calculation.discounts.find((discount) => discount.type === discountType)
    }
    return undefined
  }

  // Create array from the displayable discount types
  const discountsArray = Object.entries(DISCOUNT_CONFIG).map(([type, info]) => ({
    type: type as DisplayableDiscountType,
    amount: discountTotals[type as DisplayableDiscountType] || 0,
    textKey: (info as { textKey: string }).textKey
  }))

  return (
    <Box fullWidth bottom spacing={EIGHT_PX}>
      {isLoading ? (
        <SkeletonCustom>
          <GrooverText textKey="common.loading" customerId={client?.id} />
        </SkeletonCustom>
      ) : (
        <span style={{ color: '#333', fontWeight: 'bold' }}>{userName.toUpperCase()}</span>
      )}
      <UserContainer fullWidth fullPadding spacing="16px">
        {/* Iterate through activitites */}
        {Object.entries(
          userPrices
            ?.sort((a, b) => b.finalPrice - a.finalPrice)
            .reduce<Record<string, Calculation[]>>((acc, calculation) => {
              const key = calculation.activity.group || calculation.activity.id
              return {
                ...acc,
                [key]: [...(acc[key] || []), calculation]
              }
            }, {})
        ).map(([, calculations]) => (
          <Box fullWidth key={calculations[0].activity.id}>
            <PriceContainerActivity
              isLoading={isLoading}
              calculations={calculations}
              state={state}
              currency={currency}
              client={client}
            />
            <Box
              fullWidth
              style={{
                borderBottom: '1px solid #eee',
                paddingTop: '4px',
                paddingBottom: '4px'
              }}
            />
          </Box>
        ))}

        {/* Show user discounts  */}
        {isLoading ? (
          <SkeletonCustom>
            <DiscountRow isLoading={isLoading} label={'Student'} amount={10} currency={DEFAULT_CURRENCY} />
          </SkeletonCustom>
        ) : (
          discountsArray.map((discount) => {
            if (discount.amount > 0) {
              // For price_override type, use the actual discount name instead of the generic text
              const discountObj = getDiscount(discount.type)
              const label =
                discount.type === 'price_override' && discountObj?.name
                  ? discountObj.name
                  : getGrooverText(discount.textKey, undefined, client?.id)

              return (
                <DiscountRow
                  isLoading={isLoading}
                  key={discount.type}
                  label={label}
                  amount={discount.amount}
                  currency={currency}
                  discount={getDiscount(discount.type)}
                  client={client}
                />
              )
            }
          })
        )}

        {!isLoading && freeDropinRewardPreview && (
          <FreeDropinRewardPreview preview={freeDropinRewardPreview} client={client} />
        )}

        {/* Show user total price */}
        <Box alignSelf="flex-end" top spacing={TWELVE_PX} style={{ fontStyle: 'italic' }}>
          {isLoading ? (
            <SkeletonCustom>
              <GrooverText textKey="common.loading" customerId={client?.id} />
            </SkeletonCustom>
          ) : (
            <span style={{ color: '#333' }}>{formatPrice(totalPriceInCents, currency)}</span>
          )}
        </Box>
      </UserContainer>
    </Box>
  )
}

const DiscountRow = ({
  label,
  amount,
  currency,
  discount,
  isLoading,
  client
}: {
  label: string
  amount: number
  currency: Currency
  discount?: CalculationDiscount
  isLoading: boolean
  client?: Client
}) => (
  <Box fullWidth direction="row" align="center" justify="space-between" top bottom spacing={TWELVE_PX}>
    {isLoading ? (
      <SkeletonCustom>
        <GrooverText textKey="common.loading" customerId={client?.id} />
      </SkeletonCustom>
    ) : (
      <span style={{ color: '#333' }}>{label}</span>
    )}
    <Box direction="row" align="center" gap={FOUR_PX}>
      {/* Show relative discount value if percentage (eg. -> 10% <- (100 SEK)) */}
      {discount?.discountCalculationType === 'percentage' &&
        (isLoading ? (
          <SkeletonCustom>
            <GrooverText textKey="common.loading" customerId={client?.id} />
          </SkeletonCustom>
        ) : (
          <span style={{ color: '#333' }}>{`${discount?.discountValue || 0}%`}</span>
        ))}
      <Discount fullPadding spacing={FOUR_PX}>
        {isLoading ? (
          <SkeletonCustom>
            <GrooverText textKey="common.loading" customerId={client?.id} />
          </SkeletonCustom>
        ) : (
          <span style={{ color: 'white' }}>{`-${formatPrice(amount, currency)}`}</span>
        )}
      </Discount>
    </Box>
  </Box>
)

const UserContainer = styled(Box)`
  border-radius: 12px;
  background-color: #fcfcfc;
  border: 1px solid #f5eded;
`

export const Discount = styled(Box)`
  background-color: #70ab37;
  border-radius: 8px;
  padding: 2px 8px;
  min-width: 80px;
  text-align: right;
  white-space: nowrap;
  color: white;
`

export default UserPricing
